import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import {
  WorkflowConfig,
  WorkflowNode,
  WorkflowStatus,
  NodeType,
  createEmptyNode,
} from './pipeline-workflow.model';

const STORAGE_KEY = 'platform_pipeline_workflow';
const PENDING_WORKFLOW_DRAFT_KEY = 'platform_pending_workflow_draft';

const MOCK_CONFIG: WorkflowConfig = {
  id: 'wf-001',
  name: 'DEAM Service Approval',
  version: 'v2.3',
  status: 'DRAFT',
  lastEditedAgo: '2 hours ago',
  nodes: [
    {
      id: 'node-auto-1',
      type: 'AUTO_APPROVE',
      label: 'Small Amount Auto-Approve',
      isValid: true,
      triggerConditions: [
        { id: 'tc-1', field: 'amount', operator: '<=', value: 5000, currency: 'USD' },
      ],
      approvers: [],
      decisionRule: { type: 'ANY_ONE', description: 'Auto-routed — no human action required' },
      delegationEnabled: false,
      delegationUsers: [],
      sla: {
        reminderHours: 0,
        maxEscalations: 0,
        onTimeout: 'AUTO_APPROVE',
        escalationChain: [],
        worstCaseHours: 0,
      },
      metadata: {
        id: 'node-auto-1',
        createdAt: '2024-11-01',
        createdBy: 'admin@company.com',
        lastEdited: '3 days ago',
        editCount: 4,
        usedBy: 'DEAM Service, I/O Workflows',
      },
    },
    {
      id: 'node-parallel-2',
      type: 'PARALLEL',
      label: 'Mid-Range Parallel Review',
      isValid: true,
      hasWarning: false,
      triggerConditions: [
        { id: 'tc-2a', field: 'amount', operator: '>', value: 5000, currency: 'USD' },
        { id: 'tc-2b', field: 'amount', operator: '<=', value: 50000, currency: 'USD' },
      ],
      approvers: [
        { id: 'apr-1', name: 'Finance Team', type: 'role', memberCount: 4 },
        { id: 'apr-2', name: 'Team Lead', type: 'role', memberCount: 1 },
      ],
      decisionRule: {
        type: 'N_OF_M',
        minApprovals: 2,
        totalApprovers: 2,
        description: 'Both approvers must approve',
      },
      delegationEnabled: true,
      delegationUsers: [
        { id: 'del-1', name: 'Backup Finance Team', type: 'role', memberCount: 2 },
      ],
      sla: {
        reminderHours: 24,
        maxEscalations: 2,
        onTimeout: 'ESCALATE',
        escalationChain: [
          { id: 'esc-1', name: 'Finance Manager' },
          { id: 'esc-2', name: 'CFO' },
        ],
        worstCaseHours: 72,
      },
      metadata: {
        id: 'node-parallel-2',
        createdAt: '2024-11-15',
        createdBy: 'jane.doe@company.com',
        lastEdited: '1 day ago',
        editCount: 12,
        usedBy: 'DEAM Service',
      },
    },
    {
      id: 'node-hierarchy-3',
      type: 'HIERARCHY',
      label: 'Large Amount Chain Approval',
      isValid: false,
      hasWarning: true,
      triggerConditions: [
        { id: 'tc-3', field: 'amount', operator: '>', value: 50000, currency: 'USD' },
      ],
      approvers: [
        { id: 'apr-3', name: 'Department Head', type: 'role', memberCount: 1 },
        { id: 'apr-4', name: 'VP Finance', type: 'user', email: 'vp.finance@company.com' },
      ],
      decisionRule: {
        type: 'ALL',
        description: 'All approvers must approve in sequence',
      },
      delegationEnabled: false,
      delegationUsers: [],
      sla: {
        reminderHours: 48,
        maxEscalations: 3,
        onTimeout: 'ESCALATE',
        escalationChain: [
          { id: 'esc-3', name: 'Ops Director' },
          { id: 'esc-4', name: 'CEO' },
        ],
        worstCaseHours: 144,
      },
      metadata: {
        id: 'node-hierarchy-3',
        createdAt: '2024-12-01',
        createdBy: 'admin@company.com',
        lastEdited: '5 hours ago',
        editCount: 7,
        usedBy: 'DEAM Service',
      },
    },
  ],
};

@Injectable({ providedIn: 'root' })
export class PipelineWorkflowService {
  private readonly _config$ = new BehaviorSubject<WorkflowConfig>(this._load());
  private readonly _activeNodeId$ = new BehaviorSubject<string | null>(this._config$.getValue().nodes[0]?.id ?? null);
  private pendingDraftForCurrentLoad: Partial<WorkflowConfig> | null = null;

  readonly config$ = this._config$.asObservable();
  readonly activeNodeId$ = this._activeNodeId$.asObservable();

  get snapshot(): WorkflowConfig {
    return this._config$.getValue();
  }

  get activeNodeId(): string | null {
    return this._activeNodeId$.getValue();
  }

  get activeNode(): WorkflowNode | null {
    const activeNodeId = this.activeNodeId;
    if (!activeNodeId) return null;
    return this.snapshot.nodes.find((node) => node.id === activeNodeId) ?? null;
  }

  get activeNodeIndex(): number {
    const activeNodeId = this.activeNodeId;
    if (!activeNodeId) return -1;
    return this.snapshot.nodes.findIndex((node) => node.id === activeNodeId);
  }

  loadConfig(raw: Partial<WorkflowConfig>): void {
    const source = this.consumePendingWorkflowDraft() ?? raw;
    const merged: WorkflowConfig = {
      ...MOCK_CONFIG,
      ...source,
      nodes: Array.isArray(source.nodes) ? source.nodes : MOCK_CONFIG.nodes,
    };
    this._config$.next(merged);
    this.ensureActiveNode();
  }

  selectNode(nodeId: string | null): void {
    if (!nodeId) {
      this._activeNodeId$.next(this.snapshot.nodes[0]?.id ?? null);
      return;
    }

    const exists = this.snapshot.nodes.some((node) => node.id === nodeId);
    this._activeNodeId$.next(exists ? nodeId : (this.snapshot.nodes[0]?.id ?? null));
  }

  addNode(type: NodeType): void {
    const cfg = this.snapshot;
    const node = createEmptyNode(type, cfg.nodes.length);
    this._update({ nodes: [...cfg.nodes, node] });
    this.selectNode(node.id);
  }

  updateNode(updated: WorkflowNode): void {
    const nodes = this.snapshot.nodes.map((n) => (n.id === updated.id ? updated : n));
    this._update({ nodes });
  }

  deleteNode(id: string): void {
    const nodes = this.snapshot.nodes.filter((n) => n.id !== id);
    this._update({ nodes });
    if (this.activeNodeId === id) {
      this.selectNode(nodes[0]?.id ?? null);
    }
  }

  duplicateNode(id: string): void {
    const cfg = this.snapshot;
    const idx = cfg.nodes.findIndex((n) => n.id === id);
    if (idx === -1) return;
    const original = cfg.nodes[idx];
    const copy: WorkflowNode = {
      ...original,
      id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      label: `${original.label} (copy)`,
      isValid: false,
      metadata: original.metadata
        ? { ...original.metadata, editCount: 0, lastEdited: 'just now' }
        : undefined,
    };
    const nodes = [
      ...cfg.nodes.slice(0, idx + 1),
      copy,
      ...cfg.nodes.slice(idx + 1),
    ];
    this._update({ nodes });
    this.selectNode(copy.id);
  }

  reorderNodes(from: number, to: number): void {
    const nodes = [...this.snapshot.nodes];
    const [removed] = nodes.splice(from, 1);
    nodes.splice(to, 0, removed);
    this._update({ nodes });
  }

  publish(): Observable<void> {
    return of(void 0).pipe(
      delay(800),
      tap(() => this._update({ status: 'PUBLISHED' as WorkflowStatus })),
    );
  }

  validate(): { valid: boolean; warnings: string[] } {
    const nodes = this.snapshot.nodes;
    const warnings: string[] = [];
    nodes.forEach((n) => {
      if (n.type !== 'AUTO_APPROVE' && n.approvers.length === 0) {
        warnings.push(`Node "${n.label}" has no approvers`);
      }
    });
    return { valid: warnings.length === 0, warnings };
  }

  private _update(partial: Partial<WorkflowConfig>): void {
    const next: WorkflowConfig = { ...this.snapshot, ...partial, lastEditedAgo: 'just now' };
    this._config$.next(next);
    this.ensureActiveNode();
    this._save(next);
  }

  private _save(cfg: WorkflowConfig): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
    } catch { /* storage full or unavailable */ }
  }

  private _load(): WorkflowConfig {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw) as WorkflowConfig;
    } catch { /* parse error */ }
    return MOCK_CONFIG;
  }

  private consumePendingWorkflowDraft(): Partial<WorkflowConfig> | null {
    if (this.pendingDraftForCurrentLoad) {
      return this.pendingDraftForCurrentLoad;
    }

    try {
      const raw = sessionStorage.getItem(PENDING_WORKFLOW_DRAFT_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as Partial<WorkflowConfig>;
      sessionStorage.removeItem(PENDING_WORKFLOW_DRAFT_KEY);
      this.pendingDraftForCurrentLoad = parsed;
      setTimeout(() => {
        this.pendingDraftForCurrentLoad = null;
      });
      return parsed;
    } catch {
      return null;
    }
  }

  private ensureActiveNode(): void {
    const activeNodeId = this.activeNodeId;
    if (activeNodeId && this.snapshot.nodes.some((node) => node.id === activeNodeId)) {
      return;
    }

    this._activeNodeId$.next(this.snapshot.nodes[0]?.id ?? null);
  }
}
